import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdfjs } from '../../lib/pdfjsLoader';

type PdfPagesProps = {
  /** Fetches the PDF bytes (through the API — the bucket has no CORS). */
  load: () => Promise<Uint8Array>;
  title: string;
  /** Re-fetch when this changes (a regenerated report has a new URL). */
  reloadKey?: string | null;
  /** Shown as "Open in a new tab" when rendering fails. */
  fallbackUrl?: string | null;
};

/**
 * Every page of a PDF, stacked in one scrollable column.
 *
 * An <iframe> was the previous review surface: desktop browsers show their
 * multi-page viewer in it, but iOS Safari paints only the first page and
 * Android Chrome paints nothing — Shane read a three-page report as one page
 * (Jim, 2026-09-08). pdf.js draws each page to a canvas at the container's
 * width, so a phone scrolls the whole report.
 */
export function PdfPages({
  load,
  title,
  reloadKey,
  fallbackUrl,
}: PdfPagesProps) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState(false);
  const [ratio, setRatio] = useState(1.294); // US letter until page 1 says otherwise

  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setDoc(null);
    setError(false);
    void (async () => {
      try {
        const [lib, bytes] = await Promise.all([loadPdfjs(), load()]);
        loaded = await lib.getDocument({ data: bytes }).promise;
        const first = (await loaded.getPage(1)).getViewport({ scale: 1 });
        if (!cancelled) {
          setRatio(first.height / first.width || 1.294);
          setDoc(loaded);
        }
      } catch (err) {
        console.error('Failed to load PDF:', err);
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (loaded) void loaded.loadingTask.destroy();
    };
    // `load` is a fresh closure on every render; reloadKey names the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (error) {
    return (
      <div className="text-center text-white px-6" title={title}>
        <AlertCircle className="w-12 h-12 text-danger-400 mx-auto mb-4" />
        <p className="text-lg font-medium">Report could not be displayed</p>
        {fallbackUrl ? (
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-block px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
          >
            Open in a new tab
          </a>
        ) : null}
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="text-center text-white" title={title}>
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <p className="text-lg font-medium">Loading report…</p>
      </div>
    );
  }

  const pages = Array.from({ length: doc.numPages }, (_, i) => i + 1);
  return (
    <div
      className="w-full h-full overflow-auto overscroll-contain px-2 py-3"
      title={title}
      role="document"
    >
      <p className="text-center text-xs text-slate-400 mb-2">
        {doc.numPages} {doc.numPages === 1 ? 'page' : 'pages'}
      </p>
      {/* JIM-0929-09: cap the page width so a laptop shows more of each page */}
      <div className="flex flex-col items-center gap-3 w-full max-w-[880px] mx-auto">
        {pages.map((pageNo) => (
          <PdfPage key={pageNo} doc={doc} pageNo={pageNo} ratio={ratio} />
        ))}
      </div>
    </div>
  );
}

/** Pages within this distance of the visible area are drawn; the rest are freed. */
const NEAR_MARGIN = '1500px 0px';

/**
 * One page, drawn only while it is near the visible area (JIM-1014-17, Jim 2026-10-14: a
 * 171-page SWPPP stalled after page 1 because every page was drawn at once, at device pixels,
 * past what the browser allows canvases to hold). Until drawn, the page keeps its place at
 * page 1's proportions so the scroll bar and the page count stay honest.
 */
function PdfPage({
  doc,
  pageNo,
  ratio,
}: {
  doc: PDFDocumentProxy;
  pageNo: number;
  /** Height / width of page 1, for the placeholder. */
  ratio: number;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [near, setNear] = useState(
    () => typeof IntersectionObserver === 'undefined'
  );
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setNear(entry.isIntersecting),
      { rootMargin: NEAR_MARGIN }
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!near) {
      // Free the bitmap; the placeholder keeps the page's place.
      canvas.width = 0;
      canvas.height = 0;
      setRendered(false);
      return;
    }
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<unknown> } | null = null;
    void (async () => {
      const page = await doc.getPage(pageNo);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      // Fit the container width; draw at device pixels so text stays crisp.
      const cssWidth = Math.max(
        200,
        (boxRef.current?.clientWidth ?? base.width) - 8
      );
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({
        scale: (cssWidth / base.width) * dpr,
      });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
      // jsdom has no 2D context; a real browser always does.
      if (!canvas.getContext('2d')) return;
      task = page.render({ canvas, viewport });
      await task.promise;
      if (!cancelled) setRendered(true);
    })().catch((err) => {
      if (!cancelled) console.error(`Failed to render page ${pageNo}:`, err);
    });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageNo, near]);

  return (
    <div
      ref={boxRef}
      className="w-full flex justify-center"
      style={rendered ? undefined : { aspectRatio: `1 / ${ratio}` }}
    >
      <canvas
        ref={canvasRef}
        data-page={pageNo}
        data-rendered={rendered ? 'true' : 'false'}
        aria-label={`Page ${pageNo}`}
        className="bg-white shadow-md rounded-sm"
      />
    </div>
  );
}
